import { Currency, Table } from '@mairistem/composants';
import {
	type Exercice,
	getLibelleRechercheImputation,
	type Imputation,
	ImputationSearchInput,
	Section,
	type Sens,
} from '@mairistem/comptabilite';
import { formatMontant, Icon } from '@mairistem/finances-utils';
import Decimal from 'decimal.js';
import React from 'react';
import type { BlocageSaisieImputation } from '../../../constants/autorisation/blocageSaisieImputation';
import DECISION from '../../../constants/common/decision';
import STORAGE_KEY from '../../../constants/common/storage';
import type ProImputation from '../../../interfaces/common/proImputation';
import {
	getLibelleBlocageSaisieImputation,
	isExerciceValide,
	isProImputationSaisie,
} from '../../../utils/autorisation';

interface ProImputationItemProps {
	/** Raison pour laquelle l'imputation ne peut pas etre saisie */
	blocageSaisie: BlocageSaisieImputation | null;
	decision: number;
	exercice: number;
	exerciceHasGestionTva: boolean;
	exerciceHasReportFonc: boolean;
	/** Exercice sur lequel l'imputation est saisie (le dernier exercice si celui du financement n'existe pas encore) */
	exerciceSaisie: Exercice | null;
	/** Si un service est selectionne, seules les imputations de ses espaces budgetaires sont saisissables */
	identifiantService: number | null;
	onChangeProImputation: (data: Partial<ProImputation>) => void;
	onRemoveProImputation: () => void;
	proImputation: ProImputation;
	sens: string;
}

const ProImputationItem = ({
	blocageSaisie,
	decision,
	exercice,
	exerciceHasGestionTva,
	exerciceHasReportFonc,
	exerciceSaisie,
	identifiantService,
	onChangeProImputation,
	onRemoveProImputation,
	proImputation,
	sens,
}: ProImputationItemProps) => {
	const handleBlurProImputationMontant = (
		{ target: { name } }: React.FocusEvent<HTMLInputElement>,
		montant: number,
	) => {
		onChangeProImputation({ [name]: montant });
	};

	const resetImputation = () => {
		onChangeProImputation({
			codeAnalytique: null,
			codeArticle: null,
			codeChapitre: null,
			codeFonction: null,
			codeOperation: null,
			codeVentilation: null,
			identifiantCoImputation: null,
		});
	};

	const handleImputationChange = (imputation: Imputation) => {
		if (imputation === null) {
			resetImputation();
			return;
		}

		const newImputation = {
			codeAnalytique: imputation.analytique?.code,
			codeArticle: imputation.article?.code,
			codeChapitre: imputation.chapitre?.code,
			codeFonction: imputation.fonction?.code,
			codeOperation: imputation.operation?.code,
			codeVentilation: imputation.ventilation?.code,
			natureBudgetaire: imputation.natureBudgetaire,
			section: imputation.chapitre?.section,
		};

		// Si l'exercice du financement existe, l'imputation lui appartient
		if (exerciceSaisie?.anneeExercice === exercice) {
			onChangeProImputation({
				...newImputation,
				identifiantCoImputation: imputation.identifiant,
			});
		} else {
			onChangeProImputation(newImputation);
		}
	};

	const getDefaultImputation = () => {
		// On stock le libelle de l'imputation dans le colonne libelle chapitre
		if (!isProImputationSaisie(proImputation)) return null;

		return {
			chapitre: {
				code: proImputation.codeChapitre,
				section: proImputation.section,
			},
			libelle: getLibelleRechercheImputation(
				proImputation.codeArticle,
				proImputation.codeOperation,
				proImputation.codeFonction,
				proImputation.codeAnalytique,
				proImputation.codeVentilation,
				proImputation.natureBudgetaire,
			),
			sens,
		};
	};

	const montantsLignes = React.useMemo(
		() => ({
			engage: proImputation.lignesEngagement.reduce(
				// TODO: Faire un utilitaire pour la gestion de la TVA
				(acc, curr) =>
					acc.add(
						exerciceHasGestionTva && !curr.tvaRecup ? curr.mttc : curr.mtht,
					),
				new Decimal(0),
			),
			mandate: proImputation.lignesMouvement.reduce(
				(acc, curr) =>
					acc.add(
						exerciceHasGestionTva && !curr.tvaRecup ? curr.mttc : curr.mtht,
					),
				new Decimal(0),
			),
		}),
		[
			exerciceHasGestionTva,
			proImputation.lignesEngagement,
			proImputation.lignesMouvement,
		],
	);

	const isExerciceSaisi = isExerciceValide(exercice);

	const getPlaceholder = () => {
		if (!isExerciceSaisi) return 'Veuillez sélectionner un exercice';

		if (blocageSaisie)
			return getLibelleBlocageSaisieImputation(blocageSaisie, exerciceSaisie);

		if (exerciceSaisie.anneeExercice !== exercice)
			return `Commencez à saisir un article de l'exercice ${exerciceSaisie.anneeExercice}`;

		return 'Commencez à saisir un article';
	};

	return (
		<Table.Row>
			<Table.Cell verticalAlign="middle">
				<ImputationSearchInput
					annee={exerciceSaisie?.anneeExercice}
					dataTestid={`imputationSearchInputProImputation${proImputation.identifiant}`}
					defaultValue={getDefaultImputation()}
					disabled={!isExerciceSaisi || !!blocageSaisie}
					identifiantEntite={Number(
						sessionStorage.getItem(STORAGE_KEY.ID_ENTITE_CPTA),
					)}
					// L'exercice est toujours envoye explicitement, l'annee seule n'etant pas fiable cote API
					identifiantExercice={exerciceSaisie?.identifiant}
					identifiantService={identifiantService}
					onChange={handleImputationChange}
					placeholder={getPlaceholder()}
					section={
						decision === DECISION.AP
							? Section.INVESTISSEMENT
							: Section.FONCTIONNEMENT
					}
					sens={sens as Sens}
					searchComplement={{
						withAnalytique: true,
						withArticle: true,
						withChapitre: true,
						withFonction: true,
						withOperation: true,
						withVentilation: true,
					}}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`montantProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="montant"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.montant}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`revisionProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="revision"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.revision}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`rarNProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="rarN"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.rarN}
					disabled={!exerciceHasReportFonc && decision === DECISION.AE}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`rarN1ProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="rarN1"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.rarN1}
					disabled={!exerciceHasReportFonc && decision === DECISION.AE}
				/>
			</Table.Cell>
			<Table.Cell
				content={formatMontant(montantsLignes.engage.toNumber())}
				textAlign="right"
			/>
			<Table.Cell
				content={formatMontant(montantsLignes.mandate.toNumber())}
				textAlign="right"
			/>
			<Table.Cell textAlign="center">
				<Icon
					color="red"
					cursor="pointer"
					data-testid={`removeProImputationButton${proImputation.identifiant}`}
					iconSet="Lucide"
					name="Trash2"
					onClick={onRemoveProImputation}
					size={16}
				/>
			</Table.Cell>
		</Table.Row>
	);
};

export default ProImputationItem;
